import type { RegisterCountry, SearchKind } from '@/services/api/registers'

/**
 * Seeding the register picker from the entity the analyst right-clicked.
 *
 * Pure, so the decisions are testable without a canvas: which text to search
 * for, which half of the snapshot to search, and which country's snapshot.
 */

/** Property names a case schema might carry a company's registration number under.
 *
 *  PARTNER LIST — mirrored by `ICO_PROPERTY_CANDIDATES` in
 *  src/libs/services/spectre/services/registry/graph_mapping.py, which decides
 *  where the ičo is written. A change here needs the matching change there in
 *  the same review. */
const IDENTIFIER_KEYS = [
  'registration_id',
  'registration_number',
  'org_no',
  'company_number',
  'ico',
  'ičo',
] as const

const ICO_PATTERN = /^\d{8}$/

export interface GraphAnchor {
  id: string
  name?: string | null
  typeLabel?: string | null
  properties?: Record<string, unknown> | null
}

/** The anchor's registration number, when one of its properties looks like one. */
export function anchorIdentifier(anchor: GraphAnchor | null): string | null {
  const properties = anchor?.properties
  if (!properties) return null
  for (const key of IDENTIFIER_KEYS) {
    const value = properties[key]
    if (typeof value === 'number' && ICO_PATTERN.test(String(value))) return String(value)
    if (typeof value !== 'string') continue
    const trimmed = value.trim()
    if (trimmed) return trimmed
  }
  return null
}

/**
 * What to put in the search box when the picker opens.
 *
 * An identifier wins over a name: the API treats an 8-digit query as an exact
 * ičo match, which lands the right company outright instead of on a page of
 * namesakes. Falls back to the entity's name, and to empty when it has neither.
 */
export function initialRegisterQuery(anchor: GraphAnchor | null): string {
  return anchorIdentifier(anchor) ?? anchor?.name?.trim() ?? ''
}

/**
 * Which half of the snapshot to search.
 *
 * A Person node can only match a person. Company and Organization both search
 * companies — an Organization the analyst typed in may well be a registered
 * body, and the snapshot has no separate bucket for one.
 */
export function initialSearchKind(anchor: GraphAnchor | null): SearchKind {
  return anchor?.typeLabel === 'Person' ? 'person' : 'company'
}

/**
 * Which country's snapshot to open on.
 *
 * CZ by default — the larger of the two collections and the console page's
 * default — and whatever the analyst last switched to for the rest of the
 * session. Deliberately NOT inferred from the entity: a case entity's `country`
 * is often absent or free text, and silently searching the wrong register reads
 * as "not in the register" rather than as a wrong guess.
 */
const COUNTRY_KEY = 'spectre-register-country'

/**
 * Web Storage is optional here, in three ways.
 *
 * This module is pure logic and is unit-tested in vitest's `node` environment,
 * which has no `sessionStorage` global at all. A browser can refuse access to
 * the property (Safari private mode), and it can accept the property but throw
 * from the call (quota, disabled cookies). Forgetting which country the analyst
 * last picked is harmless in every one of those cases; throwing out of what is
 * otherwise a pure module is not — so each access is guarded rather than
 * probed once.
 */
export function preferredRegisterCountry(): RegisterCountry {
  try {
    return globalThis.sessionStorage?.getItem(COUNTRY_KEY) === 'sk' ? 'sk' : 'cz'
  } catch {
    return 'cz'
  }
}

export function rememberRegisterCountry(country: RegisterCountry): void {
  try {
    globalThis.sessionStorage?.setItem(COUNTRY_KEY, country)
  } catch {
    // Storage refused. The picker opens on CZ next time; nothing else breaks.
  }
}
